// src/pages/Dashboard.jsx

import React, { useEffect, useState, useContext, useMemo } from "react";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    LineChart,
    Line,
    Area,
    Legend,
} from "recharts";

import { AuthContext } from "../AuthContext";
import useAuthFetch from "../hooks/useAuthFetch";

import {
    FaRocket,
    FaCheckCircle,
    FaHeartbeat,
    FaClock,
    FaTachometerAlt,
    FaColumns,
} from "react-icons/fa";

const getStatusColor = (status) => {
    const key = status.toLowerCase();
    if (key === "passed") return "#22c55e";
    if (key === "failed") return "#ef4444";
    return "#fbbf24";
};
const TREND_COLOR = "#6366f1";
const FEATURE_COLOR = "#14b8a6";
const ENV_COLORS = ["#22d3ee", "#818cf8", "#f472b6", "#fb923c"];

// Drill-down modal component
function Modal({ title, children, onClose }) {
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                    onClick={onClose}
                >
                    ✕
                </button>
                <h2 className="text-xl font-bold mb-4">{title}</h2>
                {children}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { roles } = useContext(AuthContext);
    const authFetch = useAuthFetch();
    const canViewAll = roles.some((r) => ["SuperAdmin", "Admin"].includes(r));

    // ── State
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [project, setProject] = useState("");
    const [summary, setSummary] = useState(null);
    const [trend, setTrend] = useState([]);
    const [byFeature, setByFeature] = useState([]);
    const [env, setEnv] = useState([]);
    const [todayFailedCount, setTodayFailedCount] = useState(null);
    const [todayFailedScenarios, setTodayFailedScenarios] = useState([]);
    const [topFailedScenarios, setTopFailedScenarios] = useState([]);
    const [durationHist, setDurationHist] = useState([]);
    const [flakiness, setFlakiness] = useState(null);
    const [compareA, setCompareA] = useState("");
    const [compareB, setCompareB] = useState("");
    const [compareData, setCompareData] = useState(null);
    const [drillRows, setDrillRows] = useState(null);
    const [drillTitle, setDrillTitle] = useState("");

    // ── Compute status totals
    const statusTotals = useMemo(() => {
        if (!summary?.status) return {};
        return summary.status.reduce((acc, s) => {
            const key = s.status.toLowerCase();
            const bucket = key.startsWith("pas") ? "Passed" : s.status;
            acc[bucket] = (acc[bucket] || 0) + Number(s.cnt || 0);
            return acc;
        }, {});
    }, [summary]);
    const totalPassed = statusTotals["Passed"] || 0;

    // ── Fetch projects
    useEffect(() => {
        const ep = canViewAll ? "/projects" : "/projects/my";
        setProjectsLoading(true);
        authFetch
            .get(ep)
            .then((res) => setProjects(res.data.projects || []))
            .catch(() => setProjects([]))
            .finally(() => setProjectsLoading(false));
    }, [authFetch, canViewAll]);

    // ── Fetch metrics on project change
    useEffect(() => {
        if (!project) return;
        const endpoints = {
            summary: "/metrics/summary",
            trend: "/metrics/trend",
            byFeature: "/metrics/byFeature",
            env: "/metrics/env",
            todayFailedCount: "/metrics/todayFailed",
            todayFailedScenarios: "/metrics/todayFailedScenarios",
            topFailedScenarios: "/metrics/topFailedScenarios",
            durationHist: "/metrics/durationHistogram",
            flakiness: "/metrics/flakiness",
        };

        Object.entries(endpoints).forEach(([key, path]) =>
            authFetch
                .get(`${path}?project=${encodeURIComponent(project)}`)
                .then((res) => {
                    const data = res.data;
                    switch (key) {
                        case "summary":
                            setSummary(data);
                            break;
                        case "trend":
                            setTrend(data);
                            break;
                        case "byFeature":
                            setByFeature(data);
                            break;
                        case "env":
                            setEnv(data);
                            break;
                        case "todayFailedCount":
                            setTodayFailedCount(data.count);
                            break;
                        case "todayFailedScenarios":
                            setTodayFailedScenarios(data);
                            break;
                        case "topFailedScenarios":
                            setTopFailedScenarios(data);
                            break;
                        case "durationHist":
                            setDurationHist(data);
                            break;
                        case "flakiness":
                            setFlakiness(data.flakyScore);
                            break;
                        default:
                            break;
                    }
                })
                .catch((err) => console.error(`Error fetching ${key}:`, err))
        );
    }, [project, authFetch]);

    // ── Compare two periods
    useEffect(() => {
        if (!project || !compareA || !compareB) return;
        authFetch
            .get(
                `/metrics/compare?project=${encodeURIComponent(
                    project
                )}&a=${compareA}&b=${compareB}`
            )
            .then((res) => setCompareData(res.data))
            .catch((err) => console.error("compare error", err));
    }, [project, compareA, compareB, authFetch]);

    // ── Project selector view
    if (!summary) {
        return (
            <div className="p-8 bg-white">
                <h1 className="text-3xl font-bold mb-6 text-gray-800">
                    Automation Dashboard
                </h1>
                <div className="max-w-md mx-auto bg-white p-6 rounded-2xl shadow-xl">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700">
                        Select a Project
                    </h2>
                    <div className="flex flex-wrap justify-center gap-4">
                        {projectsLoading ? (
                            <p className="text-gray-600">Loading projects…</p>
                        ) : projects.length === 0 ? (
                            <p className="text-gray-600">No projects available</p>
                        ) : (
                            projects.map((p) => (
                                <button
                                    key={p.project_id}
                                    onClick={() => {
                                        setProject(p.project_name);
                                        setCompareData(null);
                                    }}
                                    className="bg-blue-500 hover:bg-cyan-700 text-white font-medium py-2 px-6 rounded-xl shadow-md transition-transform transform hover:-translate-y-0.5"
                                >
                                    {p.project_name}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Prepare chart data
    const pieData = Object.entries(statusTotals).map(([name, value]) => ({
        name,
        value,
    }));
    const trendData = trend;
    const featureData = byFeature;
    const envData = env.map((e, i) => ({
        ...e,
        color: ENV_COLORS[i % ENV_COLORS.length],
    }));
    const avgSec = Number(summary.duration.avg_s || 0);
    const maxSec = Number(summary.duration.max_s || 0);

    return (
        <div className="p-8 bg-white space-y-8">
            {/* Top-row stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard
                    title="Total Runs"
                    value={summary.total ?? 0}
                    Icon={FaRocket}
                    iconBg="bg-indigo-100"
                    iconColor="text-indigo-600"
                />
                <StatCard
                    title="Total Passed"
                    value={totalPassed}
                    Icon={FaCheckCircle}
                    iconBg="bg-green-100"
                    iconColor="text-green-600"
                />
                <StatCard
                    title="Today's Failures"
                    value={todayFailedCount ?? "–"}
                    Icon={FaHeartbeat}
                    iconBg="bg-red-100"
                    iconColor="text-red-600"
                />
                <StatCard
                    title="Avg Duration"
                    value={`${(avgSec / 60).toFixed(1)} min`}
                    Icon={FaClock}
                    iconBg="bg-yellow-100"
                    iconColor="text-yellow-600"
                />
                <StatCard
                    title="Slowest Run"
                    value={`${maxSec.toFixed(0)} s`}
                    Icon={FaTachometerAlt}
                    iconBg="bg-purple-100"
                    iconColor="text-purple-600"
                />
            </div>

            {/* Today's Failed Scenarios */}
            {todayFailedScenarios.length > 0 && (
                <ChartSection title="Today's Failed Scenarios">
                    <ul className="list-disc pl-6 space-y-1 text-red-600">
                        {todayFailedScenarios.map((name, i) => (
                            <li key={i}>{name}</li>
                        ))}
                    </ul>
                </ChartSection>
            )}

            {/* Top Failed Scenarios */}
            <ChartSection title="Top Failed Scenarios">
                <div className="bg-white rounded-2xl shadow-xl overflow-auto max-h-64">
                    <table className="min-w-full text-left">
                        <thead className="bg-gray-100 sticky top-0">
                        <tr>
                            <th className="px-4 py-2 w-12 text-gray-700">#</th>
                            <th className="px-4 py-2 text-gray-700">Scenario</th>
                            <th className="px-4 py-2 w-24 text-gray-700">Fails</th>
                        </tr>
                        </thead>
                        <tbody>
                        {topFailedScenarios.map((s, i) => (
                            <tr key={i} className="odd:bg-white even:bg-gray-50">
                                <td className="px-4 py-2 text-gray-800">{i + 1}</td>
                                <td className="px-4 py-2 text-gray-800">{s.scenario_name}</td>
                                <td className="px-4 py-2 text-gray-800">{s.fails}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </ChartSection>

            {/* Status Distribution */}
            <ChartSection title="Status Distribution">
                <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={{ fill: "#374151", fontSize: 12, fontWeight: 500 }}
                        >
                            {pieData.map((e, i) => (
                                <Cell key={i} fill={getStatusColor(e.name)} />
                            ))}
                        </Pie>
                        <Tooltip wrapperClassName="bg-white shadow rounded px-2 py-1 text-gray-700" />
                        <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            wrapperStyle={{ fontSize: 12, color: "#4b5563" }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Daily Trend */}
            <ChartSection title="Daily Executions Trend">
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart
                        data={trendData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                        <XAxis dataKey="day" stroke={TREND_COLOR} tick={{ fill: "#4b5563" }} />
                        <YAxis stroke={TREND_COLOR} tick={{ fill: "#4b5563" }} />
                        <Tooltip wrapperClassName="bg-white shadow rounded px-2 py-1 text-gray-700" />
                        <Line
                            type="monotone"
                            dataKey="cnt"
                            stroke={TREND_COLOR}
                            strokeWidth={3}
                            dot={{ r: 4 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="cnt"
                            fill={TREND_COLOR}
                            fillOpacity={0.15}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Failures by Feature */}
            <ChartSection title="Top Failure Features">
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={featureData} barCategoryGap="20%">
                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                        <XAxis dataKey="feature_name" tick={{ fill: "#4b5563", fontSize: 12 }} />
                        <YAxis tick={{ fill: "#4b5563", fontSize: 12 }} />
                        <Tooltip wrapperClassName="bg-white shadow rounded px-2 py-1 text-gray-700" />
                        <Bar
                            dataKey="fails"
                            fill={FEATURE_COLOR}
                            radius={[8, 8, 0, 0]}
                            barSize={32}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Environment Breakdown */}
            <ChartSection title="Environment Breakdown">
                <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                        <Pie
                            data={envData}
                            dataKey="cnt"
                            nameKey="environment"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={{ fill: "#374151", fontSize: 12, fontWeight: 500 }}
                        >
                            {envData.map((e, i) => (
                                <Cell key={i} fill={e.color} />
                            ))}
                        </Pie>
                        <Tooltip wrapperClassName="bg-white shadow rounded px-2 py-1 text-gray-700" />
                        <Legend
                            verticalAlign="middle"
                            align="right"
                            layout="vertical"
                            iconType="square"
                            wrapperStyle={{ fontSize: 12, color: "#4b5563" }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Run Duration Distribution */}
            <ChartSection
                title="Run Duration Distribution"
                onClick={() => {
                    setDrillTitle("Durations");
                    setDrillRows(durationHist.map((b) => `${b.bucket}: ${b.count}`));
                }}
            >
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={durationHist}>
                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f97316" />
                    </BarChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Test Stability (Flakiness) */}
            <ChartSection
                title="Test Stability (Flakiness)"
                onClick={() => {
                    setDrillTitle("Flakiness Details");
                    setDrillRows([`Flaky Score: ${(flakiness * 100).toFixed(1)}%`]);
                }}
            >
                <ResponsiveContainer width="100%" height={100}>
                    <PieChart>
                        <Pie
                            data={[
                                { name: "Stable", value: 1 - flakiness },
                                { name: "Flaky", value: flakiness },
                            ]}
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={30}
                            outerRadius={50}
                        >
                            <Cell fill="#22c55e" />
                            <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Forecast Trend */}
            <ChartSection title="Daily Executions Trend + Forecast">
                <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                        data={[
                            ...trend,
                            trend.length
                                ? {
                                    day: trend[trend.length - 1].day + 1,
                                    cnt: Math.max(
                                        Math.round(
                                            trend[trend.length - 1].cnt +
                                            (trend[trend.length - 1].cnt -
                                                trend[trend.length - 2]?.cnt ||
                                                0)
                                        ),
                                        0
                                    ),
                                    forecast: true,
                                }
                                : [],
                        ]}
                    >
                        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip
                            formatter={(v, n, p) =>
                                p.payload.forecast ? [`${v} (est)`, n] : [v, n]
                            }
                        />
                        <Line type="monotone" dataKey="cnt" stroke={TREND_COLOR} dot={{ r: 4 }} />
                        <Area type="monotone" dataKey="cnt" fill={TREND_COLOR} fillOpacity={0.1} />
                    </LineChart>
                </ResponsiveContainer>
            </ChartSection>

            {/* Compare Two Periods */}
            <ChartSection title="Compare Two Periods">
                <div className="flex flex-wrap gap-4 mb-4">
                    <input
                        type="month"
                        value={compareA}
                        onChange={(e) => setCompareA(e.target.value)}
                        className="border px-2 py-1 rounded"
                    />
                    <input
                        type="month"
                        value={compareB}
                        onChange={(e) => setCompareB(e.target.value)}
                        className="border px-2 py-1 rounded"
                    />
                </div>
                {compareData ? (
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={compareData}>
                            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                            <XAxis dataKey="period" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="passes" fill="#22c55e" />
                            <Bar dataKey="fails" fill="#ef4444" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-gray-500">Pick both months to compare</p>
                )}
            </ChartSection>

            {/* Drill-down modal */}
            {drillRows && (
                <Modal title={drillTitle} onClose={() => setDrillRows(null)}>
                    <ul className="list-disc pl-6 space-y-1 text-sm font-mono">
                        {drillRows.map((r, i) => (
                            <li key={i}>{r}</li>
                        ))}
                    </ul>
                </Modal>
            )}
        </div>
    );
}

// StatCard component
function StatCard({ title, value, Icon, iconBg, iconColor }) {
    return (
        <div className="flex items-center bg-white rounded-2xl shadow-xl p-6">
            <div className={`p-3 rounded-full ${iconBg}`}>
                <Icon className={`w-6 h-6 ${iconColor}`} />
            </div>
            <div className="ml-5">
                <p className="text-sm uppercase tracking-wide text-gray-500">{title}</p>
                <p className="mt-1 text-2xl font-semibold text-gray-800">{value}</p>
            </div>
        </div>
    );
}

// ChartSection component
function ChartSection({ title, children, onClick }) {
    return (
        <div
            className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition"
            onClick={onClick}
        >
            <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2 flex items-center gap-2">
                <FaColumns className="text-gray-500" /> {title}
            </h3>
            {children}
        </div>
    );
}
